/**
 * Settings → Workspace (super-admin only). Een zijnavigatie in drie groepen,
 * elk onderdeel een eigen weergave met titel en uitleg:
 *
 * Deze workspace
 * - Naam en toegang — de naam en het toegangswachtwoord van de huidige
 *   workspace.
 *
 * Toegang
 * - Wissel workspace — master-wachtwoord → lijst → (evt. workspace-
 *   wachtwoord) → switch.
 * - Super admins — de lijst (add/remove).
 * - Master-wachtwoord — gate voor de lijst bij "Wissel workspace".
 *
 * Installatie
 * - Workspaces — een workspace aanmaken, leeg of met het Meridiaan
 *   Bank-voorbeeld, plus archief (`WorkspacesTab.tsx`).
 * - E-mail (globaal) — de mailinstelling zonder workspace: terugval voor
 *   elke workspace en de mail waarmee inlogcodes verstuurd worden.
 * - AI — de aanmelding van de installatie bij de AI-aanbieder (WIF of
 *   API-sleutel), voor alle workspaces.
 * - Cron jobs — systeem-brede achtergrond-jobs (MITRE CWE-sync, orphan-GC):
 *   aan/uit, tijdstip/frequentie, handmatig draaien, run-geschiedenis. Zie
 *   `db/cronJobs.ts` + `cron/scheduler.ts` + `cron/runner.ts`. Cloudflare's
 *   eigen Cron Trigger blijft vast op een uurlijkse tick — deze weergave stuurt
 *   alleen de D1-laag die bepaalt of een job op die tick draait (geen
 *   Cloudflare API-token nodig, bewuste keuze 2026-07-24).
 * - Oude snapshots — snapshots van formaat 1 (van vóór migratie 0054) plus
 *   weesbestanden in R2 opruimen, met voorvertoning. Zie `db/snapshotPurge.ts`.
 *
 * Stijl: de ledger-tokens (`ledgerUi.ts`); de modal volgt de donkere modus
 * van het OS (`SettingsModal tone="ledger"`).
 *
 * Backend: `apps/worker/src/handlers/superAdmin.ts`. De override die
 * `switch` zet is een cookie-scoped virtuele `role: "admin"` (max 1 uur,
 * zie CLAUDE.md) — geen echte membership-rij.
 */

import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type CronJobDTO,
  type CronJobRunDTO,
  type LegacySnapshotsDTO,
  type SuperAdminRef,
  type SuperAdminWorkspaceRef,
} from "../api/client";
import { confirmDialog } from "../editor/confirmDialog";
import { useSession } from "../state/session";
import { AiAuthSettings } from "./AiAuthSettings";
import { ld, pill } from "./ledgerUi";
import { MailConfigSettings } from "./MailConfigSettings";
import { WorkspacesTab } from "./WorkspacesTab";

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

type SuperAdminTab =
  | "workspace-password"
  | "master-password"
  | "admins"
  | "switch"
  | "workspaces"
  | "global-mail"
  | "ai"
  | "cron-jobs"
  | "legacy-snapshots";

interface Props {
  onClose: () => void;
}

/** Een id van 36 tekens is in een lijst alleen ruis; de eerste acht volstaan
 *  om er één terug te vinden. Het volledige id staat in de tooltip. */
function shortId(id: string): string {
  return id.length > 9 ? `${id.slice(0, 8)}…` : id;
}

function Loading() {
  const { t } = useTranslation();
  return <p className={ld.muted}>{t("settings.common.loading")}</p>;
}

function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className={ld.error}>
      {children}
    </p>
  );
}

export function SuperAdminSettings({ onClose }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<SuperAdminTab>("switch");
  const workspaceId = useSession((s) => s.workspaceId);
  const me = useSession((s) => s.me);
  const workspaceName =
    me?.super_admin_override?.workspace_id === workspaceId
      ? me.super_admin_override.workspace_name
      : (me?.workspaces.find((w) => w.workspace_id === workspaceId)?.workspace_name ??
        workspaceId ??
        "");

  const groups: Array<{ label: string; items: Array<{ id: SuperAdminTab; label: string }> }> = [
    {
      label: t("settings.superAdmin.navGroupThis"),
      items: [{ id: "workspace-password", label: t("settings.superAdmin.navThisWorkspace") }],
    },
    {
      label: t("settings.superAdmin.navGroupAccess"),
      items: [
        { id: "switch", label: t("settings.superAdmin.tabSwitch") },
        { id: "admins", label: t("settings.superAdmin.tabAdmins") },
        { id: "master-password", label: t("settings.superAdmin.tabMasterPassword") },
      ],
    },
    {
      label: t("settings.superAdmin.navGroupInstall"),
      items: [
        { id: "workspaces", label: t("settings.superAdmin.tabWorkspaces") },
        { id: "global-mail", label: t("settings.tabs.globalMail") },
        { id: "ai", label: t("settings.tabs.ai") },
        { id: "cron-jobs", label: t("settings.superAdmin.tabCronJobs") },
        { id: "legacy-snapshots", label: t("settings.superAdmin.tabLegacySnapshots") },
      ],
    },
  ];

  const views: Record<SuperAdminTab, { title: string; intro: string }> = {
    "workspace-password": {
      title: workspaceName,
      intro: t("settings.superAdmin.thisWorkspaceIntro"),
    },
    switch: { title: t("settings.superAdmin.tabSwitch"), intro: t("settings.superAdmin.intro") },
    admins: {
      title: t("settings.superAdmin.tabAdmins"),
      intro: t("settings.superAdmin.adminsIntro"),
    },
    "master-password": {
      title: t("settings.superAdmin.tabMasterPassword"),
      intro: t("settings.superAdmin.masterPasswordHint"),
    },
    workspaces: {
      title: t("settings.superAdmin.tabWorkspaces"),
      intro: t("settings.superAdmin.workspacesIntro"),
    },
    "global-mail": {
      title: t("settings.tabs.globalMail"),
      intro: t("settings.superAdmin.globalMailIntro"),
    },
    ai: { title: t("settings.tabs.ai"), intro: t("settings.aiAuth.intro") },
    "cron-jobs": {
      title: t("settings.superAdmin.tabCronJobs"),
      intro: t("settings.superAdmin.cronJobs.intro"),
    },
    "legacy-snapshots": {
      title: t("settings.superAdmin.tabLegacySnapshots"),
      intro: t("settings.superAdmin.legacySnapshots.intro"),
    },
  };
  const view = views[tab];

  return (
    <div className="flex flex-col md:flex-row">
      <nav
        aria-label={t("settings.superAdmin.navAria")}
        className="shrink-0 border-(--ld-border) border-b bg-(--ld-raised) md:w-56 md:border-r md:border-b-0"
      >
        {/* Telefoon: één rij die zijwaarts scrolt; vanaf md: de groepen onder elkaar. */}
        <div className="flex gap-1 overflow-x-auto px-3 py-2 md:flex-col md:gap-0 md:overflow-visible md:px-3 md:py-4">
          {groups.map((group, gi) => (
            <div key={group.label} className="contents md:block">
              <p
                className={`hidden px-2.5 pb-1.5 text-(--ld-muted) text-xs md:block ${gi === 0 ? "" : "md:mt-5"}`}
              >
                {group.label}
              </p>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  aria-current={tab === item.id ? "page" : undefined}
                  onClick={() => setTab(item.id)}
                  className={`flex h-8 shrink-0 items-center whitespace-nowrap rounded-md px-2.5 text-left text-sm transition-colors md:w-full ${
                    tab === item.id
                      ? "bg-(--ld-active) font-medium text-(--ld-text)"
                      : "text-(--ld-muted) hover:bg-(--ld-active) hover:text-(--ld-text)"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </nav>

      <section
        aria-labelledby="super-admin-view-title"
        className="min-w-0 flex-1 px-5 py-5 md:px-7"
      >
        <h3 id="super-admin-view-title" className={ld.viewTitle}>
          {view.title}
        </h3>
        <p className={ld.viewIntro}>{view.intro}</p>
        <div className="mt-6">
          {tab === "workspace-password" && workspaceId && (
            <WorkspacePasswordTab workspaceId={workspaceId} workspaceName={workspaceName} />
          )}
          {tab === "master-password" && <MasterPasswordTab />}
          {tab === "admins" && <AdminsTab />}
          {tab === "switch" && <SwitchTab onClose={onClose} />}
          {tab === "workspaces" && <WorkspacesTab onClose={onClose} />}
          {tab === "global-mail" && <MailConfigSettings workspaceId={null} />}
          {tab === "ai" && <AiAuthSettings />}
          {tab === "cron-jobs" && <CronJobsTab />}
          {tab === "legacy-snapshots" && <LegacySnapshotsTab />}
        </div>
      </section>
    </div>
  );
}

// ── Shared set/change/clear password form ───────────────────────────

function PasswordForm({
  idPrefix,
  isSet,
  onSubmit,
  onClear,
}: {
  idPrefix: string;
  isSet: boolean;
  onSubmit: (newPassword: string, currentPassword: string | undefined) => Promise<void>;
  onClear: (currentPassword: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!next) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await onSubmit(next, isSet ? current : undefined);
      setInfo(t("settings.superAdmin.passwordSaved"));
      setCurrent("");
      setNext("");
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await onClear(current);
      setInfo(t("settings.superAdmin.passwordCleared"));
      setCurrent("");
      setNext("");
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="flex max-w-md flex-col gap-4">
      {isSet && (
        <div>
          <label htmlFor={`${idPrefix}-current`} className={ld.label}>
            {t("settings.superAdmin.currentPasswordLabel")}
          </label>
          <input
            id={`${idPrefix}-current`}
            type="password"
            autoComplete="current-password"
            className={ld.input}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
      )}
      <div>
        <label htmlFor={`${idPrefix}-new`} className={ld.label}>
          {t("settings.superAdmin.newPasswordLabel")}
        </label>
        <input
          id={`${idPrefix}-new`}
          type="password"
          autoComplete="new-password"
          aria-describedby={`${idPrefix}-rules`}
          className={ld.input}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <p id={`${idPrefix}-rules`} className={ld.hint}>
          {t("settings.superAdmin.passwordHint")}
        </p>
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      {info && (
        <p role="status" className={ld.success}>
          {info}
        </p>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={busy || !next} className={ld.primary}>
          {isSet ? t("settings.superAdmin.changeButton") : t("settings.superAdmin.setButton")}
        </button>
        {isSet && (
          <button
            type="button"
            disabled={busy || !current}
            onClick={() => void clear()}
            className={ld.danger}
          >
            {t("settings.superAdmin.clearButton")}
          </button>
        )}
      </div>
    </form>
  );
}

function SetPill({ isSet }: { isSet: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={isSet ? pill.positive : pill.neutral}>
      {isSet ? t("settings.superAdmin.statusSet") : t("settings.superAdmin.statusNotSet")}
    </span>
  );
}

// ── Deze workspace: naam en toegangswachtwoord ──────────────────────

function WorkspacePasswordTab({
  workspaceId,
  workspaceName,
}: {
  workspaceId: string;
  workspaceName: string;
}) {
  const { t } = useTranslation();
  const [isSet, setIsSet] = useState<boolean | null>(null);
  const [name, setName] = useState(workspaceName);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renamed, setRenamed] = useState(false);

  const refresh = useCallback(() => {
    void api
      .superAdminStatus(workspaceId)
      .then((res) => setIsSet(!!res.workspaceAccessPasswordSet));
  }, [workspaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setName(workspaceName);
  }, [workspaceName]);

  const saveName = async (e: FormEvent) => {
    e.preventDefault();
    const next = name.trim();
    if (!next || next === workspaceName) return;
    setRenaming(true);
    setRenameError(null);
    setRenamed(false);
    try {
      await api.renameWorkspace(workspaceId, next);
      // De naam staat in de sessie (header, switcher); zonder verversen blijft
      // de oude staan tot de volgende paginalading.
      await useSession.getState().hydrate();
      setRenamed(true);
    } catch (err) {
      setRenameError(explain(err));
    } finally {
      setRenaming(false);
    }
  };

  if (isSet === null) return <Loading />;

  return (
    <div className="flex flex-col gap-8">
      <form onSubmit={(e) => void saveName(e)} className="max-w-xl">
        <label htmlFor="sa-ws-name" className={ld.sectionTitle}>
          {t("settings.superAdmin.workspaceNameTitle")}
        </label>
        <div className="mt-2 flex gap-2">
          <input
            id="sa-ws-name"
            aria-describedby="sa-ws-name-hint"
            className={`${ld.inputInline} min-w-0 flex-1`}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="submit"
            className={ld.primary}
            disabled={renaming || !name.trim() || name.trim() === workspaceName}
          >
            {renaming ? t("settings.common.saving") : t("settings.common.save")}
          </button>
        </div>
        <p id="sa-ws-name-hint" className={ld.hint}>
          {t("settings.superAdmin.workspaceNameHint")}
        </p>
        {renameError && (
          <div className="mt-3">
            <ErrorLine>{renameError}</ErrorLine>
          </div>
        )}
        {renamed && (
          <p role="status" className={`mt-3 ${ld.success}`}>
            {t("settings.superAdmin.workspaceNameSaved")}
          </p>
        )}
      </form>

      <div className="border-(--ld-rule) border-t pt-8">
        <div className="flex items-center gap-2">
          <h4 className={ld.sectionTitle}>
            {t("settings.superAdmin.workspacePasswordFor", { name: workspaceName })}
          </h4>
          <SetPill isSet={isSet} />
        </div>
        <p className={`${ld.sectionIntro} mb-4 max-w-xl`}>
          {t("settings.superAdmin.workspacePasswordHint")}
        </p>
        <PasswordForm
          idPrefix="sa-ws-pw"
          isSet={isSet}
          onSubmit={async (next, current) => {
            await api.setWorkspaceAccessPassword(workspaceId, next, current);
            refresh();
          }}
          onClear={async (current) => {
            await api.clearWorkspaceAccessPassword(workspaceId, current);
            refresh();
          }}
        />
      </div>
    </div>
  );
}

// ── Master-wachtwoord ───────────────────────────────────────────────

function MasterPasswordTab() {
  const [isSet, setIsSet] = useState<boolean | null>(null);

  const refresh = useCallback(() => {
    void api.superAdminStatus().then((res) => setIsSet(res.masterPasswordSet));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (isSet === null) return <Loading />;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <SetPill isSet={isSet} />
      </div>
      <PasswordForm
        idPrefix="sa-master-pw"
        isSet={isSet}
        onSubmit={async (next, current) => {
          await api.setMasterPassword(next, current);
          refresh();
        }}
        onClear={async (current) => {
          await api.clearMasterPassword(current);
          refresh();
        }}
      />
    </div>
  );
}

// ── Super admins ────────────────────────────────────────────────────

function AdminsTab() {
  const { t } = useTranslation();
  const myEmail = useSession((s) => s.me?.email?.toLowerCase());
  const [admins, setAdmins] = useState<SuperAdminRef[] | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void api
      .listSuperAdmins()
      .then((res) => setAdmins(res.admins))
      .catch((err) => setError(explain(err)));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.addSuperAdmin(newEmail.trim());
      setAdmins(res.admins);
      setNewEmail("");
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (email: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.removeSuperAdmin(email);
      setAdmins(res.admins);
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  if (admins === null && !error) return <Loading />;
  const list = admins ?? [];

  return (
    <div className="flex max-w-xl flex-col gap-4">
      <ul className={ld.list} aria-label={t("settings.superAdmin.adminsHeading")}>
        {list.map((a) => (
          <li key={a.email} className="flex min-h-11 items-center justify-between gap-3 px-4 py-2">
            <span className="flex min-w-0 items-center gap-2 text-sm">
              <span className="truncate">{a.email}</span>
              {a.email.toLowerCase() === myEmail && (
                <span className={pill.neutral}>{t("settings.superAdmin.you")}</span>
              )}
            </span>
            <button
              type="button"
              disabled={busy || list.length <= 1}
              onClick={() => void remove(a.email)}
              className={ld.textDanger}
              title={list.length <= 1 ? t("settings.superAdmin.removeLastError") : undefined}
            >
              {t("settings.superAdmin.removeButton")}
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={(e) => void add(e)}>
        <label htmlFor="sa-admin-add" className={ld.label}>
          {t("settings.superAdmin.addAdminLabel")}
        </label>
        <div className="mt-2 flex gap-2">
          <input
            id="sa-admin-add"
            type="email"
            autoComplete="off"
            placeholder={t("settings.superAdmin.addEmailPlaceholder")}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className={`${ld.inputInline} min-w-0 flex-1`}
          />
          <button type="submit" disabled={busy || !newEmail.trim()} className={ld.primary}>
            {t("settings.superAdmin.addButton")}
          </button>
        </div>
      </form>
      {error && <ErrorLine>{error}</ErrorLine>}
    </div>
  );
}

// ── Wissel workspace ────────────────────────────────────────────────

function SwitchTab({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const hydrate = useSession((s) => s.hydrate);
  const currentWorkspaceId = useSession((s) => s.workspaceId);
  const [masterPasswordSet, setMasterPasswordSet] = useState<boolean | null>(null);
  const [masterPassword, setMasterPassword] = useState("");
  const [workspaces, setWorkspaces] = useState<SuperAdminWorkspaceRef[] | null>(null);
  const [pendingWorkspace, setPendingWorkspace] = useState<SuperAdminWorkspaceRef | null>(null);
  const [workspacePassword, setWorkspacePassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.superAdminStatus().then((res) => setMasterPasswordSet(res.masterPasswordSet));
  }, []);

  // Bewust GEEN useCallback met lege deps hier — dat zou `masterPassword`
  // permanent vastzetten op de waarde bij mount (stale closure), zodat de
  // knop altijd de eerste (lege) waarde zou versturen i.p.v. wat de
  // gebruiker heeft getypt. Dit is een plain function, elke render vers,
  // dus altijd de actuele state.
  const reveal = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.listSuperAdminWorkspaces(masterPassword || undefined);
      setWorkspaces(res.workspaces);
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  // Geen master-wachtwoord ingesteld → lijst meteen tonen (spec). Draait
  // alleen op de `masterPasswordSet`-transitie (masterPassword is per
  // definitie leeg in dit pad, dus geen staleness-risico hier).
  useEffect(() => {
    if (masterPasswordSet === false) {
      void reveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterPasswordSet]);

  const doSwitch = async (ws: SuperAdminWorkspaceRef, accessPassword: string | undefined) => {
    setBusy(true);
    setError(null);
    try {
      await api.switchSuperAdminWorkspace(ws.id, {
        ...(masterPassword ? { masterPassword } : {}),
        ...(accessPassword ? { workspaceAccessPassword: accessPassword } : {}),
      });
      await hydrate();
      onClose();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const pick = (ws: SuperAdminWorkspaceRef) => {
    setError(null);
    if (!ws.hasAccessPassword) {
      void doSwitch(ws, undefined);
      return;
    }
    setPendingWorkspace(ws);
    setWorkspacePassword("");
  };

  if (masterPasswordSet === null) return <Loading />;

  if (workspaces === null) {
    return (
      <form
        className="flex max-w-md flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (masterPassword) void reveal();
        }}
      >
        <div>
          <label htmlFor="sa-switch-master" className={ld.label}>
            {t("settings.superAdmin.switchMasterPasswordLabel")}
          </label>
          <input
            id="sa-switch-master"
            type="password"
            autoComplete="current-password"
            placeholder={t("settings.superAdmin.switchMasterPasswordPlaceholder")}
            value={masterPassword}
            onChange={(e) => setMasterPassword(e.target.value)}
            className={ld.input}
          />
        </div>
        {error && <ErrorLine>{error}</ErrorLine>}
        <div>
          <button type="submit" disabled={busy || !masterPassword} className={ld.primary}>
            {t("settings.superAdmin.switchRevealButton")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorLine>{error}</ErrorLine>}
      <ul className={ld.list}>
        {workspaces.map((ws) => {
          const isCurrent = ws.id === currentWorkspaceId;
          const pending = pendingWorkspace?.id === ws.id;
          return (
            <li key={ws.id} className="px-4 py-2.5">
              <div className="flex min-h-8 items-center justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="truncate font-medium text-sm">{ws.name}</span>
                  <span className={ld.mono} title={ws.id}>
                    {shortId(ws.id)}
                  </span>
                  {isCurrent && (
                    <span className={pill.neutral}>{t("settings.superAdmin.current")}</span>
                  )}
                  {ws.hasAccessPassword && (
                    <span className={pill.notice}>{t("settings.superAdmin.needsPassword")}</span>
                  )}
                </div>
                {!isCurrent && !pending && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => pick(ws)}
                    className={ld.secondarySm}
                  >
                    {t("settings.superAdmin.switchButton")}
                  </button>
                )}
              </div>
              {pending && (
                <form
                  className="mt-3 mb-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (workspacePassword) void doSwitch(ws, workspacePassword);
                  }}
                >
                  <label htmlFor={`sa-switch-pw-${ws.id}`} className={ld.label}>
                    {t("settings.superAdmin.switchWorkspaceAccessLabel")}
                  </label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      id={`sa-switch-pw-${ws.id}`}
                      type="password"
                      autoComplete="off"
                      // biome-ignore lint/a11y/noAutofocus: het veld verschijnt pas na de klik op Wissel
                      autoFocus
                      value={workspacePassword}
                      onChange={(e) => setWorkspacePassword(e.target.value)}
                      className={`${ld.inputInline} min-w-0 flex-1`}
                    />
                    <button
                      type="submit"
                      disabled={busy || !workspacePassword}
                      className={ld.primary}
                    >
                      {busy
                        ? t("settings.superAdmin.switchingButton")
                        : t("settings.superAdmin.switchButton")}
                    </button>
                    <button
                      type="button"
                      className={ld.secondary}
                      onClick={() => setPendingWorkspace(null)}
                    >
                      {t("settings.superAdmin.cancel")}
                    </button>
                  </div>
                </form>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Cron jobs ─────────────────────────────────────────────────────────
//
// Opslag blijft UTC (server, `cron_jobs.hour_utc` + `cron_job_runs.
// started_at`) — deze weergave toont/bewerkt alles in Europe/Amsterdam-tijd
// en rekent onderwater om. Amsterdam heeft nooit een half-uur-offset
// (CET/CEST, beide hele uren), dus een simpele uur-offset-berekening
// per instant volstaat — geen date-library nodig.

const AMSTERDAM_TZ = "Europe/Amsterdam";

/** Amsterdam UTC-offset in hele uren voor een gegeven instant (+1 CET,
 *  +2 CEST) — bepaald via Intl i.p.v. hardcoded, dus DST-correct. */
function amsterdamOffsetHours(instant: Date): number {
  const ref = new Date(
    Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate(), 12, 0, 0),
  );
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: AMSTERDAM_TZ,
    hour: "numeric",
    hour12: false,
  });
  const localHour = Number.parseInt(fmt.format(ref), 10) % 24;
  return localHour - 12;
}

function utcHourToAmsterdamHour(hourUtc: number): number {
  const offset = amsterdamOffsetHours(new Date());
  return (((hourUtc + offset) % 24) + 24) % 24;
}

function amsterdamHourToUtcHour(localHour: number): number {
  const offset = amsterdamOffsetHours(new Date());
  return (((localHour - offset) % 24) + 24) % 24;
}

/** `iso` is een naive UTC-timestamp uit D1 (`datetime('now')`, geen
 *  tijdzone-suffix) — vandaar de `Z`-toevoeging vóór het parsen. */
function formatAmsterdam(iso: string): string {
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  return new Intl.DateTimeFormat("nl-NL", {
    timeZone: AMSTERDAM_TZ,
    dateStyle: "short",
    timeStyle: "medium",
  }).format(d);
}

function jobLabel(t: (key: string) => string, name: string): string {
  if (name === "mitre_cwe_sync") return t("settings.superAdmin.cronJobs.jobMitre");
  if (name === "orphan_gc") return t("settings.superAdmin.cronJobs.jobGc");
  return name;
}

function LegacySnapshotsTab() {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<LegacySnapshotsDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    setError(null);
    void api
      .listLegacySnapshots()
      .then(setPlan)
      .catch((err) => setError(explain(err)));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const purge = async () => {
    if (!plan) return;
    const question = t("settings.superAdmin.legacySnapshots.confirm", {
      snapshots: plan.snapshots.length,
      files: plan.orphan_files,
    });
    if (!(await confirmDialog({ message: question }))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.purgeLegacySnapshots(plan.snapshots.length, plan.orphan_files);
      setDone(
        t("settings.superAdmin.legacySnapshots.done", {
          snapshots: res.snapshots_deleted,
          files: res.files_deleted,
        }),
      );
      refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  if (plan === null && !error) return <Loading />;
  const empty = plan !== null && plan.snapshots.length === 0 && plan.orphan_files === 0;

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorLine>{error}</ErrorLine>}
      {done && (
        <p role="status" className={ld.success}>
          {done}
        </p>
      )}
      {empty && (
        <p className={`${ld.well} text-(--ld-muted) text-sm`}>
          {t("settings.superAdmin.legacySnapshots.empty")}
        </p>
      )}
      {plan && !empty && (
        <>
          <p className="text-sm">
            {t("settings.superAdmin.legacySnapshots.summary", {
              snapshots: plan.snapshots.length,
              files: plan.orphan_files,
            })}
          </p>
          {plan.snapshots.length > 0 && (
            <div className="max-h-72 overflow-auto rounded-lg border border-(--ld-border)">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-(--ld-surface)">
                  <tr className="border-(--ld-border) border-b text-left text-(--ld-muted) text-xs">
                    <th className="px-4 py-2 font-normal">
                      {t("settings.superAdmin.legacySnapshots.colDate")}
                    </th>
                    <th className="px-4 py-2 font-normal">
                      {t("settings.superAdmin.legacySnapshots.colScope")}
                    </th>
                    <th className="px-4 py-2 font-normal">
                      {t("settings.superAdmin.legacySnapshots.colLabel")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {plan.snapshots.map((s) => (
                    <tr key={s.id} className="border-(--ld-rule) border-b last:border-b-0">
                      <td className="whitespace-nowrap px-4 py-2 text-(--ld-muted) tabular-nums">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-(--ld-muted)">
                        {s.scope_kind ?? "workspace"}
                      </td>
                      <td className="max-w-[280px] truncate px-4 py-2">{s.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <button
              type="button"
              disabled={busy}
              onClick={() => void purge()}
              className={ld.danger}
            >
              {t("settings.superAdmin.legacySnapshots.purge")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CronJobsTab() {
  const [jobs, setJobs] = useState<CronJobDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void api
      .listCronJobs()
      .then((res) => setJobs(res.jobs))
      .catch((err) => setError(explain(err)));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleExpand = (name: string) => setExpanded(expanded === name ? null : name);

  if (jobs === null) return error ? <ErrorLine>{error}</ErrorLine> : <Loading />;

  return (
    <div className="flex flex-col gap-4">
      {error && <ErrorLine>{error}</ErrorLine>}
      {info && (
        <p role="status" className={ld.notice}>
          {info}
        </p>
      )}
      <ul className="flex flex-col gap-3">
        {jobs.map((job) => (
          <CronJobRow
            key={job.name}
            job={job}
            expanded={expanded === job.name}
            onToggleExpand={() => toggleExpand(job.name)}
            onChanged={refresh}
            onError={setError}
            onInfo={setInfo}
          />
        ))}
      </ul>
    </div>
  );
}

function CronJobRow({
  job,
  expanded,
  onToggleExpand,
  onChanged,
  onError,
  onInfo,
}: {
  job: CronJobDTO;
  expanded: boolean;
  onToggleExpand: () => void;
  onChanged: () => void;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [hourDraft, setHourDraft] = useState(String(utcHourToAmsterdamHour(job.hourUtc)));
  const [freqDraft, setFreqDraft] = useState(String(job.frequencyDays));
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState<CronJobRunDTO[]>([]);
  const [runsBusy, setRunsBusy] = useState(false);
  const idBase = `sa-cron-${job.name}`;

  useEffect(() => {
    setHourDraft(String(utcHourToAmsterdamHour(job.hourUtc)));
    setFreqDraft(String(job.frequencyDays));
  }, [job.hourUtc, job.frequencyDays]);

  const loadRuns = useCallback(() => {
    setRunsBusy(true);
    void api
      .listCronJobRuns(job.name)
      .then((res) => setRuns(res.runs))
      .catch(() => setRuns([]))
      .finally(() => setRunsBusy(false));
  }, [job.name]);

  useEffect(() => {
    if (expanded) loadRuns();
  }, [expanded, loadRuns]);

  // Lichte polling zolang de nieuwste run 'running' is — nodig sinds
  // `mitre_cwe_sync` async gedispatched wordt (GitHub Actions doet de
  // fetch/parse, post pas later terug via het ingest-endpoint). Zonder
  // dit blijft de tabel op "Bezig" staan totdat de gebruiker zelf ververst.
  const latestRunStatus = runs[0]?.status;
  useEffect(() => {
    if (!expanded || latestRunStatus !== "running") return;
    const id = setInterval(loadRuns, 5000);
    return () => clearInterval(id);
  }, [expanded, latestRunStatus, loadRuns]);

  const patch = async (body: { enabled?: boolean; hourUtc?: number; frequencyDays?: number }) => {
    setBusy(true);
    onError("");
    try {
      await api.updateCronJob(job.name, body);
      onChanged();
    } catch (err) {
      onError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const commitHour = () => {
    const localHour = Number.parseInt(hourDraft, 10);
    if (!Number.isInteger(localHour) || localHour < 0 || localHour > 23) {
      setHourDraft(String(utcHourToAmsterdamHour(job.hourUtc)));
      return;
    }
    const hourUtc = amsterdamHourToUtcHour(localHour);
    if (hourUtc !== job.hourUtc) void patch({ hourUtc });
  };

  const commitFreq = () => {
    const v = Number.parseInt(freqDraft, 10);
    if (Number.isInteger(v) && v >= 1 && v !== job.frequencyDays) void patch({ frequencyDays: v });
    else setFreqDraft(String(job.frequencyDays));
  };

  const runNow = async () => {
    setBusy(true);
    onError("");
    onInfo("");
    try {
      const result = await api.runCronJobNow(job.name);
      if (result.status === "running") {
        // Async gedispatched (mitre_cwe_sync via GitHub Actions) — nog
        // geen eindresultaat, dat komt later via het ingest-endpoint.
        onInfo(t("settings.superAdmin.cronJobs.dispatchedInfo"));
      }
      onChanged();
      if (expanded) loadRuns();
    } catch (err) {
      onError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const numberField =
    "h-8 w-14 rounded-md border border-(--ld-control) bg-(--ld-surface) px-2 text-right text-(--ld-text) text-sm tabular-nums focus:border-(--ld-accent) focus:outline-none focus:ring-1 focus:ring-(--ld-accent) disabled:opacity-60";

  return (
    <li className={ld.panel}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span id={`${idBase}-name`} className={ld.sectionTitle}>
              {jobLabel(t, job.name)}
            </span>
            {!job.enabled && (
              <span className={pill.neutral}>{t("settings.superAdmin.cronJobs.disabledPill")}</span>
            )}
          </div>
          <p className="mt-0.5 text-(--ld-muted) text-[13px] tabular-nums">
            {job.lastRunAt
              ? t("settings.superAdmin.cronJobs.lastRun", {
                  when: formatAmsterdam(job.lastRunAt),
                })
              : t("settings.superAdmin.cronJobs.neverRun")}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={job.enabled}
          aria-labelledby={`${idBase}-name`}
          disabled={busy}
          onClick={() => void patch({ enabled: !job.enabled })}
          className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
            job.enabled ? "bg-(--ld-primary)" : "bg-(--ld-control)"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-(--ld-surface) transition-transform ${
              job.enabled ? "translate-x-4" : ""
            }`}
          />
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
        <label className="flex items-center gap-2" htmlFor={`${idBase}-hour`}>
          <span className="text-(--ld-muted)">{t("settings.superAdmin.cronJobs.hourLabel")}</span>
          <input
            id={`${idBase}-hour`}
            type="number"
            min={0}
            max={23}
            value={hourDraft}
            disabled={busy}
            onChange={(e) => setHourDraft(e.target.value)}
            onBlur={commitHour}
            className={numberField}
            title={t("settings.superAdmin.cronJobs.hourUtcTooltip", { hourUtc: job.hourUtc })}
          />
          <span className="text-(--ld-muted)">{t("settings.superAdmin.cronJobs.tzLabel")}</span>
        </label>
        <label className="flex items-center gap-2" htmlFor={`${idBase}-freq`}>
          <span className="text-(--ld-muted)">{t("settings.superAdmin.cronJobs.everyLabel")}</span>
          <input
            id={`${idBase}-freq`}
            type="number"
            min={1}
            value={freqDraft}
            disabled={busy}
            onChange={(e) => setFreqDraft(e.target.value)}
            onBlur={commitFreq}
            className={numberField}
          />
          <span className="text-(--ld-muted)">{t("settings.superAdmin.cronJobs.daysLabel")}</span>
        </label>
        <div className="ml-auto flex items-center gap-4">
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            className={ld.textButton}
          >
            {expanded
              ? t("settings.superAdmin.cronJobs.hideHistory")
              : t("settings.superAdmin.cronJobs.showHistory")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void runNow()}
            className={ld.secondarySm}
          >
            {t("settings.superAdmin.cronJobs.runNowButton")}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-(--ld-rule) border-t pt-3">
          {runsBusy && runs.length === 0 ? (
            <Loading />
          ) : runs.length === 0 ? (
            <p className={ld.muted}>{t("settings.superAdmin.cronJobs.noRuns")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-(--ld-muted) text-xs">
                    <th className="py-1.5 pr-4 font-normal">{t("settings.maintenance.colWhen")}</th>
                    <th className="py-1.5 pr-4 font-normal">
                      {t("settings.maintenance.colTrigger")}
                    </th>
                    <th className="py-1.5 pr-4 font-normal">
                      {t("settings.maintenance.colStatus")}
                    </th>
                    <th className="py-1.5 font-normal">
                      {t("settings.superAdmin.cronJobs.colSummary")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-(--ld-rule) border-t">
                      <td className="whitespace-nowrap py-2 pr-4 tabular-nums">
                        {formatAmsterdam(r.started_at)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-4 text-(--ld-muted)">
                        {r.trigger_kind === "cron"
                          ? t("settings.superAdmin.cronJobs.triggerCron")
                          : t("settings.superAdmin.cronJobs.triggerManual")}
                      </td>
                      <td className="py-2 pr-4">
                        {r.status === "success" ? (
                          <span className={pill.positive}>
                            {t("settings.superAdmin.cronJobs.statusSuccess")}
                          </span>
                        ) : r.status === "error" ? (
                          <span className={pill.negative} title={r.error ?? ""}>
                            {t("settings.superAdmin.cronJobs.statusError")}
                          </span>
                        ) : r.possiblyStuck ? (
                          <span
                            className={pill.notice}
                            title={t("settings.superAdmin.cronJobs.possiblyStuckTooltip")}
                          >
                            {t("settings.superAdmin.cronJobs.possiblyStuck")}
                          </span>
                        ) : (
                          <span className={pill.neutral}>
                            {t("settings.superAdmin.cronJobs.running")}
                          </span>
                        )}
                      </td>
                      <td
                        className="max-w-[240px] truncate py-2 text-(--ld-muted)"
                        title={r.summary_json ?? ""}
                      >
                        {r.summary_json ? r.summary_json.slice(0, 80) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
